'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import PropTypes from 'prop-types';
import Raven from 'raven-js';
import { GYPSUM, OLAF } from 'HubStyleTokens/colors';
import I18n from 'I18n';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedNumber from 'I18n/components/FormattedNumber';
import getLang from 'I18n/utils/getLang';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import unescapedText from 'I18n/utils/unescapedText';
import AbstractTextElement from 'UIComponents/elements/AbstractTextElement';
import { H2, H3 } from 'UIComponents/elements/headings';
import HR from 'UIComponents/elements/HR';
import Small from 'UIComponents/elements/Small';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIBanner from 'UIComponents/alert/UIBanner';
import UIButton from 'UIComponents/button/UIButton';
import UIBreakString from 'UIComponents/text/UIBreakString';
import UICardSection from 'UIComponents/card/UICardSection';
import UICardWrapper from 'UIComponents/card/UICardWrapper';
import UIDescriptionList from 'UIComponents/list/UIDescriptionList';
import UIExpandableText from 'UIComponents/text/UIExpandableText';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIGrid from 'UIComponents/grid/UIGrid';
import UIGridItem from 'UIComponents/grid/UIGridItem';
import UIHelpIcon from 'UIComponents/icon/UIHelpIcon';
import UIImage from 'UIComponents/image/UIImage';
import UIIntersectionObserver from 'UIComponents/observer/UIIntersectionObserver';
import UILink from 'UIComponents/link/UILink';
import UIList from 'UIComponents/list/UIList';
import UIListingPage from 'UIComponents/page/UIListingPage';
import UISection from 'UIComponents/section/UISection';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UITruncateString from 'UIComponents/text/UITruncateString';
import * as PlanType from 'ecosystem-marketplace-core/constants/pricings/PlanType';
import * as RequestStates from 'ecosystem-marketplace-core/constants/RequestState';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { formatCentsToDollars } from 'ecosystem-marketplace-core/utils/currency';
import { getAssetBusinessTypeI18nKey, getAssetPageTypeI18nKey, getAssetPageFeatureI18nKey } from 'ecosystem-marketplace-core/utils/i18n';
import { isFulfilled, isSucceeded, isUninitialized } from 'ecosystem-marketplace-core/utils/requestState';
import { MAIN_SECTION_PADDING_Y } from 'ecosystem-marketplace-core/constants/layout';
import { getCmsFreeSignupUrl } from 'ecosystem-marketplace-core/assets/utils/urls';
import { useAssetInstallability } from 'ecosystem-marketplace-core/assets/hooks/useAssetInstallability';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import { ResponsiveInnerSection } from 'ecosystem-marketplace-core/components/layout/InnerSection';
import MainSection from 'ecosystem-marketplace-core/components/layout/MainSection';
import MainSectionInner from 'ecosystem-marketplace-core/components/layout/MainSectionInner';
import MediaQuery from 'ecosystem-marketplace-core/components/layout/MediaQuery';
import PlanCompatibilityMessage from 'ecosystem-marketplace-core/compatibility/components/PlanCompatibilityMessage';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import ThemeContactInfo from 'ecosystem-marketplace-core/components/themes/ThemeContactInfo';
import ThemeDetailsResponse from 'ecosystem-marketplace-core/records/ThemeDetailsResponse';
import ThemeProviderProfile from 'ecosystem-marketplace-core/components/themes/ThemeProviderProfile';
import ThemeResources from 'ecosystem-marketplace-core/components/themes/ThemeResources';
import ThemeSupport from 'ecosystem-marketplace-core/components/themes/ThemeSupport';
import * as AssetUninstallableReason from '../../constants/AssetUninstallableReason';
import * as PortalScope from '../../constants/PortalScope';
import * as ThemeQueryField from '../../constants/ThemeQueryField';
import { getAcademyCertifications } from '../../actions/CertificationsActions';
import { getBeforeUnloadTracker, getUsageTracker } from '../../utils/usageTracker';
import { getCertifications, getCertificationsRequestState } from '../../selectors/certifications';
import { getHasZorseWebticketScope } from '../../selectors/auth';
import { getAppContextCoreType, getIsInApp, PublicAppContext } from '../../selectors/app';
import { selectPortalScopes, selectUserScopes } from '../../selectors/auth';
import { getPortalScopes } from '../../actions/AuthActions';
import { getThemeContents } from '../../clients/productMarketplaceClient';
import { getThemeEventSelectorId } from '../../utils/hsAnalytics';
import { openHelpWidget } from '../../utils/zorse';
import { useEmbedContext } from '../../hooks/useEmbedContext';
import { useMissingPortalScopes } from '../../hooks/useMissingPortalScopes';
import BigText from 'ecosystem-marketplace-core/components/elements/BigText';
import CurrencyText from './CurrencyText';
import ExpandableTagsSection from './ExpandableTagsSection';
import FeatureCompatibilityModal from './FeatureCompatibilityModal';
import HubsParamsTracker from '../HubsParamsTracker';
import MissingScopesMessage from './MissingScopesMessage';
import Reviews from '../reviews/Reviews';
import RouterFilterTagButton from '../storefront/RouterFilterTagButton';
import ThemeClientExamples from './ThemeClientExamples';
import ThemeContents from './ThemeContents';
import ThemeContentsResponse from '../../records/ThemeContentsResponse';
import ThemeDemoButton from './ThemeDemoButton';
import ThemeDetailsDocumentHead from './documentHead/ThemeDetailsDocumentHead';
import ThemeDetailsHeader from './ThemeDetailsHeader';
import ThemeDetailsUsageTrackerContext from '../../context/ThemeDetailsUsageTrackerContext';
import ThemeDownloadButton from './ThemeDownloadButton';
import ThemeFeatures from './ThemeFeatures';
import ThemeRecommendationsSection from './ThemeRecommendationsSection';
import ThemeScreenshots from './ThemeScreenshots';
import ThemeSetupGuideButton from './ThemeSetupGuideButton';
import ThemeShareButton from './ThemeShareButton';
import ThemeVersionHistoryModalPanel from './ThemeVersionHistoryModalPanel';
import UsageTrackerContext from '../../context/UsageTrackerContext';
const PORTAL_SCOPES = Object.values(PortalScope);
const MISSING_SCOPES_LINK = 'https://knowledge.hubspot.com/resources/how-to-purchase-a-template';
const CERTIFICATIONS_LEARN_MORE_LINK = 'https://academy.hubspot.com/certification-overview';

const NoopExternalIcon = () => null;

const ExternalLink = props => /*#__PURE__*/_jsx(UILink, Object.assign({}, props, {
  ExternalIcon: NoopExternalIcon,
  external: true
}));

const Section = ({
  children,
  label
}) => /*#__PURE__*/_jsxs(Fragment, {
  children: [/*#__PURE__*/_jsx("dt", {
    children: label
  }), /*#__PURE__*/_jsx("dd", {
    children: children
  })]
});

const ThemeDetailsPage = ({
  themeDetails,
  use
}) => {
  const {
    screen
  } = useContext(UsageTrackerContext);
  const {
    isEmbedded
  } = useEmbedContext();
  const isAdminPreview = use === 'admin-preview';
  const isInApp = useSelector(getIsInApp);
  const isPreview = use === 'preview' || isAdminPreview;
  const lang = getLang();
  const providerCertifications = useSelector(getCertifications);
  const certificationsRequestState = useSelector(getCertificationsRequestState);
  const hasZorseWebticketScope = useSelector(getHasZorseWebticketScope);
  const dispatch = useDispatch();
  const [themeContents, setThemeContents] = useState(new ThemeContentsResponse());
  const [reviewsFulfilled, setReviewsFulfilled] = useState(false);
  const {
    cmsBannerButtonWidth,
    isMobileView,
    leftColumnClassName,
    rightColumnInnerClassName
  } = useResponsiveProps({
    cmsBannerButtonWidth: {
      sm: '100px',
      md: '125px',
      lg: '85px'
    },
    isMobileView: {
      [ScreenSizes.XS]: true,
      [ScreenSizes.SM]: false
    },
    leftColumnClassName: {
      md: 'p-right-10',
      lg: 'p-right-15'
    },
    rightColumnInnerClassName: {
      lg: 'p-x-4',
      xl: 'p-x-8'
    }
  });
  const [featureCompatibilityModalOpen, setFeatureCompatibilityModalOpen] = useState(false);
  const [themeContentsRequestState, setThemeContentsRequestState] = useState(RequestStates.UNINITIALIZED);
  const themeScopes = themeContents.scopes ? themeContents.scopes.toArray() : undefined;
  const invalidPortalScopes = useMemo(() => {
    return (themeScopes || []).filter(scope => !PORTAL_SCOPES.includes(scope));
  }, [themeScopes]);
  const [invalidPortalScopesRequestState, setInvalidPortalScopesRequestState] = useState(RequestStates.UNINITIALIZED);
  const {
    benefits,
    businessTypes,
    casesUrl,
    chatUrl,
    clientExamples,
    exampleUrl,
    facebookUrl,
    features,
    forumUrl,
    name,
    offering: {
      homemade,
      installsCount,
      firstPublishedAt,
      slug
    },
    offeringId,
    pageFeatures,
    pageTypes,
    phone,
    pricing,
    privacy2Url,
    privacy3Url,
    privacyUrl,
    provider,
    reviewSummary,
    s3BucketKey,
    screenshots,
    setupUrl,
    support,
    supportEmail,
    supportLanguages,
    supportUrl,
    tags,
    termsUrl,
    tiers,
    thumbnailUrl,
    versionNumber,
    versionPublishedAt,
    videoUrl
  } = themeDetails;
  const missingPortalScopes = useMissingPortalScopes(themeScopes);
  const appContext = useSelector(getAppContextCoreType);
  const userScopes = useSelector(selectUserScopes);
  const portalScopes = useSelector(selectPortalScopes);
  const {
    assetUninstallableReason,
    requestState: assetInstallabilityRequestState
  } = useAssetInstallability({
    appContext,
    offeringId,
    portalScopes,
    userScopes
  });
  useEffect(() => {
    if (isSucceeded(themeContentsRequestState) && invalidPortalScopes.length && isUninitialized(invalidPortalScopesRequestState)) {
      setInvalidPortalScopesRequestState(RequestStates.PENDING);
      dispatch(getPortalScopes(invalidPortalScopes)).then(() => {
        setInvalidPortalScopesRequestState(RequestStates.SUCCEEDED);
      }).catch(() => {
        setInvalidPortalScopesRequestState(RequestStates.FAILED);
      });
    }
  }, [dispatch, invalidPortalScopes, invalidPortalScopesRequestState, themeContentsRequestState]);
  const signupUrl = getCmsFreeSignupUrl(slug);
  useEffect(() => {
    if (isSucceeded(themeContentsRequestState) && invalidPortalScopes.length) {
      invalidPortalScopes.forEach(scope => {
        Raven.captureMessage(`Invalid theme scope - ${scope}`, {
          level: 'warning',
          extra: {
            scope
          }
        });
      });
    }
  }, [invalidPortalScopes, themeContentsRequestState]);
  const {
    planType,
    cents
  } = pricing;
  const isFree = planType === PlanType.FREE;
  const {
    name: providerName
  } = provider;
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const compatibilityType = useMemo(() => {
    if (missingPortalScopes.length) {
      if (isFree) {
        return 'COMPATIBLE_SOME';
      }

      return 'COMPATIBLE_NONE';
    } else {
      return 'COMPATIBLE_ALL';
    }
  }, [isFree, missingPortalScopes]);
  const missingFeatures = useMemo(() => {
    return missingPortalScopes.map(scope => {
      const scopeFeatureI18nKey = `marketplace-assets-ui.scopeFeatures.${scope}`;

      if (isValidI18nKey(scopeFeatureI18nKey)) {
        return I18n.text(scopeFeatureI18nKey, {
          locale: 'en'
        });
      }

      return null;
    });
  }, [missingPortalScopes]);
  const hasFeatures = useMemo(() => {
    const themeFeatures = [];
    const {
      contents
    } = themeContents;
    const hasSections = contents.some(contentItem => contentItem.fileType === 'TEMPLATE' && contentItem.templateType === 'section');

    if (hasSections) {
      themeFeatures.push('sections');
    }

    if (themeScopes) {
      themeScopes.forEach(scope => {
        const scopeFeatureI18nKey = `marketplace-assets-ui.scopeFeatures.${scope}`;

        if (isValidI18nKey(scopeFeatureI18nKey)) {
          themeFeatures.push(I18n.text(scopeFeatureI18nKey, {
            locale: 'en'
          }));
        }
      });
    }

    return themeFeatures;
  }, [themeContents, themeScopes]);
  const getEventProperties = useCallback(() => {
    return {
      screen,
      slug,
      type: 'theme',
      industry: businessTypes.toArray(),
      hasFeatures,
      homemade,
      offeringId,
      orderTotal: isFree ? 0 : formatCentsToDollars(cents),
      isInstallableByUser: assetUninstallableReason !== AssetUninstallableReason.MISSING_MARKETPLACE_ASSET_ACCESS,
      isNotInstallableReason: assetUninstallableReason || undefined,
      missingFeatures
    };
  }, [assetUninstallableReason, businessTypes, cents, hasFeatures, homemade, isFree, offeringId, missingFeatures, screen, slug]);
  const trackEvent = useCallback((eventName, eventProperties = {}) => {
    getUsageTracker().track(eventName, Object.assign({}, getEventProperties(), {}, eventProperties));
  }, [getEventProperties]);
  const trackBeforeUnloadEvent = useCallback((eventName, eventProperties = {}) => {
    getBeforeUnloadTracker().track(eventName, Object.assign({}, getEventProperties(), {}, eventProperties));
  }, [getEventProperties]);
  const trackBeforeUnloadInteraction = useCallback((action, eventProperties = {}) => {
    trackBeforeUnloadEvent('theme-details-interaction', Object.assign({}, eventProperties, {
      action
    }));
  }, [trackBeforeUnloadEvent]);
  const trackPageview = useCallback(eventProperties => {
    trackEvent('theme-details-pageview', eventProperties);
  }, [trackEvent]);
  const trackInteraction = useCallback((action, eventProperties = {}) => {
    trackEvent('theme-details-interaction', Object.assign({}, eventProperties, {
      action
    }));
  }, [trackEvent]);
  const renderSupportLanguageTag = useCallback(langCode => /*#__PURE__*/_jsx(RouterFilterTagButton, {
    disabled: isPreview,
    onClick: () => trackInteraction('click filter tag', {
      filter: ThemeQueryField.LANGUAGES,
      value: langCode
    }),
    queryField: ThemeQueryField.LANGUAGES,
    value: langCode,
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `SharedI18nStrings.languageNames.${langCode}`
    })
  }, `supported-language-${langCode}`), [isPreview, trackInteraction]);
  const renderedCertifications = useMemo(() => {
    if (themeDetails.provider.certifications.size) {
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(UIFlex, {
          align: "start",
          "aria-label": unescapedText(`marketplace-assets-ui.themes.details.certificationsAriaLabel`),
          as: "ul",
          className: "m-top-2 m-bottom-0 p-left-0",
          direction: "row",
          justify: "start",
          style: {
            listStyle: 'none'
          },
          wrap: "wrap",
          children: themeDetails.provider.certifications.map(certification => {
            const certificationData = providerCertifications.get(certification);

            if (certificationData) {
              const certificationDisplayName = certificationData.getDisplayName(lang);
              return /*#__PURE__*/_jsx("li", {
                children: /*#__PURE__*/_jsx(UITooltip, {
                  title: certificationDisplayName,
                  children: /*#__PURE__*/_jsx(UIImage, {
                    alt: certificationDisplayName,
                    className: "m-right-1 m-bottom-1",
                    height: 28,
                    width: 28,
                    src: certificationData.iconUrl
                  })
                }, certification)
              });
            }

            return null;
          })
        }), /*#__PURE__*/_jsx(UILink, {
          "aria-label": unescapedText('marketplace-assets-ui.themes.details.providerInfo.ariaLabel'),
          external: true,
          href: CERTIFICATIONS_LEARN_MORE_LINK,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-assets-ui.themes.details.providerInfo.learnMore"
          })
        })]
      });
    }

    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "marketplace-assets-ui.themes.details.providerInfo.noCertifications"
    });
  }, [lang, providerCertifications, themeDetails]);
  useEffect(() => {
    if (!isAdminPreview && isUninitialized(certificationsRequestState)) {
      dispatch(getAcademyCertifications());
    }
  }, [certificationsRequestState, dispatch, isAdminPreview]);
  useEffect(() => {
    if (!isAdminPreview && s3BucketKey && isUninitialized(themeContentsRequestState)) {
      setThemeContentsRequestState(RequestStates.PENDING);
      getThemeContents(s3BucketKey).then(resp => {
        setThemeContents(new ThemeContentsResponse(resp));
        setThemeContentsRequestState(RequestStates.SUCCEEDED);
      }).catch(error => {
        console.error('error fetching theme contents\n', error);
        setThemeContents(new ThemeContentsResponse());
        setThemeContentsRequestState(RequestStates.FAILED);
      });
    }
  }, [themeContentsRequestState, s3BucketKey, isAdminPreview]);
  /* eslint-disable react-hooks/exhaustive-deps */

  useEffect(() => {
    if (isFulfilled(themeContentsRequestState) && isFulfilled(assetInstallabilityRequestState)) {
      trackPageview();
    }
  }, [assetInstallabilityRequestState, themeContentsRequestState]);
  /* eslint-enable react-hooks/exhaustive-deps */

  return /*#__PURE__*/_jsx(ThemeDetailsUsageTrackerContext.Provider, {
    value: {
      trackEvent,
      trackBeforeUnloadEvent,
      trackBeforeUnloadInteraction,
      trackInteraction,
      trackPageview
    },
    children: /*#__PURE__*/_jsxs(NavMarker, {
      name: "THEME_DETAILS_LOAD",
      children: [/*#__PURE__*/_jsx(ThemeDetailsDocumentHead, {
        ready: reviewsFulfilled,
        themeDetails: themeDetails
      }), /*#__PURE__*/_jsxs(UIListingPage, {
        contentAreaFlush: true,
        headerComponent: /*#__PURE__*/_jsx(MainSectionInner, {
          children: /*#__PURE__*/_jsx(ThemeDetailsHeader, {
            exampleUrl: exampleUrl,
            isEmbedded: isEmbedded,
            isPreview: isPreview,
            name: name,
            onTrackInteraction: trackInteraction,
            onTrackPageview: trackPageview,
            pricing: pricing,
            providerName: providerName,
            reviewSummary: reviewSummary,
            themeDetails: themeDetails,
            themeScopes: themeScopes
          })
        }),
        mainSectionBackgroundColor: OLAF,
        pageBackgroundColor: GYPSUM,
        pageLayout: "full-width",
        children: [/*#__PURE__*/_jsx(MainSection, {
          responsive: true,
          children: /*#__PURE__*/_jsx(MainSectionInner, {
            children: /*#__PURE__*/_jsxs(UIGrid, {
              children: [/*#__PURE__*/_jsxs(UIGridItem, {
                className: leftColumnClassName,
                size: {
                  xs: 12,
                  md: 8
                },
                children: [isInApp && missingPortalScopes.length && (!invalidPortalScopes.length || isSucceeded(invalidPortalScopesRequestState)) ? /*#__PURE__*/_jsx(ResponsiveInnerSection, {
                  children: /*#__PURE__*/_jsx(UIAlert, {
                    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "marketplace-assets-ui.themes.details.missingScopesAlert.title"
                    }),
                    type: "warning",
                    children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                      message: "marketplace-assets-ui.themes.details.missingScopesAlert.message_jsx",
                      elements: {
                        Link: ExternalLink
                      },
                      options: {
                        href: MISSING_SCOPES_LINK
                      }
                    })
                  })
                }) : null, /*#__PURE__*/_jsx(PublicAppContext, {
                  children: /*#__PURE__*/_jsx(UIBanner, {
                    children: /*#__PURE__*/_jsxs(UIFlex, {
                      align: "center",
                      direction: isMobileView ? 'column' : 'row',
                      justify: "between",
                      wrap: "nowrap",
                      children: [/*#__PURE__*/_jsx("b", {
                        children: isFree ? /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.signUpBanner.freeTitle"
                        }) : /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.signUpBanner.paidTitle"
                        })
                      }), /*#__PURE__*/_jsx(UIButton, {
                        className: isMobileView ? '' : 'm-left-8',
                        "data-test-id": "theme-details-signup-banner-button",
                        href: signupUrl,
                        onClick: () => trackBeforeUnloadInteraction('click cms sign up link'),
                        size: "extra-small",
                        style: {
                          display: 'block',
                          width: cmsBannerButtonWidth
                        },
                        use: "primary-white",
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.signUpBanner.signUp"
                        })
                      })]
                    })
                  })
                }), /*#__PURE__*/_jsx(ResponsiveInnerSection, {
                  children: /*#__PURE__*/_jsx(ThemeScreenshots, {
                    name: name,
                    onTrackInteraction: trackInteraction,
                    onTrackPageview: trackPageview,
                    providerName: providerName,
                    screenshots: screenshots.toArray(),
                    videoUrl: videoUrl,
                    themeDetails: themeDetails,
                    themeScopes: themeScopes
                  })
                }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                  id: "overview",
                  children: [/*#__PURE__*/_jsx(H3, {
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "marketplace-assets-ui.themes.details.overview.title"
                    })
                  }), /*#__PURE__*/_jsx(UIExpandableText, {
                    buttonAlign: "left",
                    maxHeight: 400,
                    children: /*#__PURE__*/_jsx(UIBreakString, {
                      children: /*#__PURE__*/_jsx(BigText, {
                        children: /*#__PURE__*/_jsx(SanitizedText, {
                          body: benefits
                        })
                      })
                    })
                  })]
                }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                  id: "features",
                  children: [/*#__PURE__*/_jsx(H3, {
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "marketplace-assets-ui.themes.details.features.title"
                    })
                  }), /*#__PURE__*/_jsx(BigText, {
                    children: /*#__PURE__*/_jsx(ThemeFeatures, {
                      features: features.toArray(),
                      onTrackInteraction: trackInteraction
                    })
                  })]
                }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                  id: "contents",
                  children: [/*#__PURE__*/_jsx(H3, {
                    children: /*#__PURE__*/_jsx(FormattedMessage, {
                      message: "marketplace-assets-ui.themes.details.contents.title"
                    })
                  }), /*#__PURE__*/_jsx(ThemeContents, {
                    onTrackInteraction: trackInteraction,
                    requestState: themeContentsRequestState,
                    themeContents: themeContents
                  })]
                })]
              }), /*#__PURE__*/_jsxs(UIGridItem, {
                size: {
                  xs: 12,
                  md: 4
                },
                children: [/*#__PURE__*/_jsx(MediaQuery, {
                  minScreenSize: ScreenSizes.MD,
                  children: /*#__PURE__*/_jsx(ResponsiveInnerSection, {
                    children: /*#__PURE__*/_jsxs(UICardWrapper, {
                      className: "m-bottom-0 p-all-5 p-bottom-6",
                      compact: true,
                      style: {
                        marginTop: -1 * (124 + MAIN_SECTION_PADDING_Y)
                      },
                      children: [/*#__PURE__*/_jsx(UICardSection, {
                        className: "text-center",
                        children: /*#__PURE__*/_jsx(AbstractTextElement, {
                          className: "is--heading-1",
                          children: isFree ? /*#__PURE__*/_jsx(FormattedMessage, {
                            message: "marketplace-assets-ui.themes.details.downloadCard.free"
                          }) : /*#__PURE__*/_jsx(CurrencyText, {
                            cents: pricing.cents
                          })
                        })
                      }), /*#__PURE__*/_jsx(UICardSection, {
                        children: /*#__PURE__*/_jsxs(UIFlex, {
                          direction: 'column',
                          align: "stretch",
                          children: [/*#__PURE__*/_jsx(ThemeDownloadButton, {
                            className: "m-bottom-3",
                            "data-test-id": "theme-download-button",
                            section: "header",
                            themeDetails: themeDetails,
                            themeScopes: themeScopes
                          }), /*#__PURE__*/_jsx(ThemeDemoButton, {
                            className: "m-left-0",
                            exampleUrl: exampleUrl,
                            name: name,
                            onTrackInteraction: trackInteraction,
                            onTrackPageview: trackPageview,
                            modalPrimaryButton: /*#__PURE__*/_jsx(ThemeDownloadButton, {
                              themeDetails: themeDetails,
                              themeScopes: themeScopes
                            }),
                            providerName: providerName,
                            section: "header",
                            themeDetails: themeDetails,
                            themeScopes: themeScopes
                          })]
                        })
                      }), /*#__PURE__*/_jsxs(UICardSection, {
                        className: "text-center",
                        children: [/*#__PURE__*/_jsx(ThemeSetupGuideButton, {
                          setupUrl: setupUrl,
                          style: {
                            width: 'auto'
                          }
                        }), /*#__PURE__*/_jsx(ThemeShareButton, {
                          section: "header",
                          style: {
                            width: 'auto'
                          },
                          use: "link"
                        })]
                      })]
                    })
                  })
                }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                  className: rightColumnInnerClassName,
                  children: [/*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                    children: [/*#__PURE__*/_jsx(H3, {
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.themes.details.themeInfo.title"
                      })
                    }), /*#__PURE__*/_jsxs(UIDescriptionList, {
                      children: [/*#__PURE__*/_jsx("dt", {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.themeInfo.installs"
                        })
                      }), /*#__PURE__*/_jsx("dd", {
                        children: installsCount > 0 ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
                          message: "marketplace-assets-ui.themes.details.themeInfo.installsBucket.bucket_jsx",
                          elements: {
                            Number: FormattedNumber
                          },
                          options: {
                            value: installsCount
                          }
                        }) : /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.themeInfo.installsBucket.min"
                        })
                      }), /*#__PURE__*/_jsx("dt", {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.themeInfo.created"
                        })
                      }), /*#__PURE__*/_jsx("dd", {
                        children: firstPublishedAt != null ? /*#__PURE__*/_jsx(FormattedDateTime, {
                          value: firstPublishedAt,
                          format: "ll"
                        }) : /*#__PURE__*/_jsx("span", {
                          children: "\u2014"
                        })
                      }), /*#__PURE__*/_jsx("dt", {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.themeInfo.version",
                          options: {
                            versionNumber
                          }
                        })
                      }), /*#__PURE__*/_jsx("dd", {
                        children: /*#__PURE__*/_jsxs(UIList, {
                          children: [/*#__PURE__*/_jsx("span", {
                            children: /*#__PURE__*/_jsx(FormattedDateTime, {
                              value: versionPublishedAt,
                              format: "ll"
                            })
                          }), /*#__PURE__*/_jsx("span", {
                            children: /*#__PURE__*/_jsx(UIButton, {
                              use: "link",
                              onClick: () => {
                                setVersionHistoryOpen(true);
                                trackInteraction('click version history link', {
                                  section: 'sidebar about'
                                });
                              },
                              children: /*#__PURE__*/_jsx(FormattedMessage, {
                                message: "marketplace-assets-ui.themes.details.themeInfo.versionHistory"
                              })
                            })
                          })]
                        })
                      }), /*#__PURE__*/_jsx("dt", {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.themeInfo.pageTypes"
                        })
                      }), /*#__PURE__*/_jsx("dd", {
                        children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
                          isPreview: isPreview,
                          onTrackInteraction: trackInteraction,
                          queryField: ThemeQueryField.PAGE_TYPES,
                          tags: pageTypes,
                          translateTag: getAssetPageTypeI18nKey
                        })
                      }), /*#__PURE__*/_jsx("dt", {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.themeInfo.pageFeatures"
                        })
                      }), /*#__PURE__*/_jsx("dd", {
                        children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
                          isPreview: isPreview,
                          onTrackInteraction: trackInteraction,
                          queryField: ThemeQueryField.PAGE_FEATURES,
                          tags: pageFeatures,
                          translateTag: getAssetPageFeatureI18nKey
                        })
                      }), /*#__PURE__*/_jsx("dt", {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.themeInfo.businessTypes"
                        })
                      }), /*#__PURE__*/_jsx("dd", {
                        children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
                          isPreview: isPreview,
                          onTrackInteraction: trackInteraction,
                          queryField: ThemeQueryField.BUSINESS_TYPES,
                          tags: businessTypes,
                          translateTag: getAssetBusinessTypeI18nKey
                        })
                      }), tags.size > 0 && /*#__PURE__*/_jsxs(Fragment, {
                        children: [/*#__PURE__*/_jsx("dt", {
                          children: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: "marketplace-assets-ui.themes.details.themeInfo.searchTags"
                          })
                        }), /*#__PURE__*/_jsx("dd", {
                          children: /*#__PURE__*/_jsx(ExpandableTagsSection, {
                            isPreview: isPreview,
                            onTrackInteraction: trackInteraction,
                            queryField: ThemeQueryField.TAGS,
                            tags: tags
                          })
                        })]
                      })]
                    })]
                  }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                    children: [/*#__PURE__*/_jsx(H3, {
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.themes.details.requirements.title"
                      })
                    }), /*#__PURE__*/_jsx(UISection, {
                      children: /*#__PURE__*/_jsx(UIDescriptionList, {
                        children: /*#__PURE__*/_jsx(Section, {
                          label: /*#__PURE__*/_jsx(FormattedMessage, {
                            message: 'marketplace-assets-ui.themes.details.requirements.compatibilityHeadingText'
                          }),
                          children: /*#__PURE__*/_jsx(PlanCompatibilityMessage, {
                            appContext: isInApp ? 'app' : 'public',
                            compatibilityType: compatibilityType,
                            helpText: compatibilityType !== 'COMPATIBLE_ALL' ? /*#__PURE__*/_jsx(UIHelpIcon, {
                              title: /*#__PURE__*/_jsx(MissingScopesMessage, {
                                missingPortalScopes: missingPortalScopes
                              })
                            }) : null,
                            onFeaturesLinkClick: () => setFeatureCompatibilityModalOpen(true),
                            onTrackInteraction: trackInteraction,
                            popoverHeadingText: /*#__PURE__*/_jsx(FormattedMessage, {
                              message: "marketplace-assets-ui.themes.details.requirements.popoverHeading"
                            }),
                            tiers: tiers,
                            tierGroupNames: ['CRM', 'CMS', 'MARKETING', 'SALES', 'SERVICE', 'OPERATIONS']
                          })
                        })
                      })
                    })]
                  }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                    children: [/*#__PURE__*/_jsx(H3, {
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.themes.details.providerInfo.title",
                        options: {
                          providerName
                        }
                      })
                    }), /*#__PURE__*/_jsx(UISection, {
                      children: /*#__PURE__*/_jsx(ThemeProviderProfile, {
                        onTrackInteraction: (action, eventProperties = {}) => {
                          trackInteraction(action, Object.assign({}, eventProperties, {
                            section: 'sidebar provider'
                          }));
                        },
                        providerWebsiteAnalyticsId: getThemeEventSelectorId('SIDEBAR-PROVIDER-WEBSITE'),
                        provider: provider,
                        use: "sidebar"
                      })
                    }), /*#__PURE__*/_jsxs(UIDescriptionList, {
                      children: [/*#__PURE__*/_jsx("dt", {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.providerInfo.certifications"
                        })
                      }), /*#__PURE__*/_jsx("dd", {
                        children: renderedCertifications
                      })]
                    })]
                  }), /*#__PURE__*/_jsx(ResponsiveInnerSection, {
                    children: /*#__PURE__*/_jsx(ThemeResources, {
                      casesUrl: casesUrl,
                      forumUrl: forumUrl,
                      getThemeEventSelectorId: getThemeEventSelectorId,
                      onTrackInteraction: action => trackInteraction(action, {
                        section: 'sidebar resources'
                      }),
                      setupUrl: setupUrl,
                      supportUrl: supportUrl
                    })
                  }), /*#__PURE__*/_jsx(ResponsiveInnerSection, {
                    children: /*#__PURE__*/_jsx(ThemeContactInfo, {
                      chatUrl: chatUrl,
                      facebookUrl: facebookUrl,
                      getThemeEventSelectorId: getThemeEventSelectorId,
                      onTrackInteraction: action => trackInteraction(action, {
                        section: 'sidebar contact'
                      }),
                      phone: phone,
                      provider: provider,
                      supportEmail: supportEmail
                    })
                  }), /*#__PURE__*/_jsxs(ResponsiveInnerSection, {
                    children: [/*#__PURE__*/_jsx(H3, {
                      children: /*#__PURE__*/_jsx(FormattedMessage, {
                        message: "marketplace-assets-ui.themes.details.termsOfService.title"
                      })
                    }), /*#__PURE__*/_jsx("p", {
                      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                        message: "marketplace-assets-ui.themes.details.termsOfService.message_jsx",
                        elements: {
                          Link: ExternalLink
                        },
                        options: {
                          privacyPolicyLink: {
                            href: privacyUrl,
                            onClick: () => trackInteraction('click privacy policy link', {
                              section: 'sidebar terms'
                            })
                          },
                          termsLink: {
                            href: termsUrl,
                            onClick: () => trackInteraction('click terms link', {
                              section: 'sidebar terms'
                            })
                          }
                        }
                      })
                    }), privacy2Url || privacy3Url ? /*#__PURE__*/_jsxs(Fragment, {
                      children: [/*#__PURE__*/_jsx(Small, {
                        children: /*#__PURE__*/_jsx(FormattedMessage, {
                          message: "marketplace-assets-ui.themes.details.termsOfService.otherPrivacyInformation"
                        })
                      }), /*#__PURE__*/_jsx(UIList, {
                        children: [privacy2Url, privacy3Url].map((otherPrivacyUrl, index) => otherPrivacyUrl ? /*#__PURE__*/_jsx(ExternalLink, {
                          href: otherPrivacyUrl,
                          onClick: () => trackInteraction('click other privacy policy link', {
                            section: 'sidebar terms'
                          }),
                          children: /*#__PURE__*/_jsx(UITruncateString, {
                            children: otherPrivacyUrl
                          })
                        }, `privacy-link-${index}`) : null)
                      })]
                    }) : null]
                  }), hasZorseWebticketScope && /*#__PURE__*/_jsxs(Fragment, {
                    children: [/*#__PURE__*/_jsx(HR, {}), /*#__PURE__*/_jsx("p", {
                      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                        message: "marketplace-assets-ui.themes.details.report.message_jsx",
                        options: {
                          onClick: () => {
                            trackInteraction('click report this theme link', {
                              section: 'sidebar terms'
                            });
                            openHelpWidget({
                              create: true
                            });
                          }
                        },
                        elements: {
                          Link: UILink
                        }
                      })
                    })]
                  })]
                })]
              })]
            })
          })
        }), !!clientExamples.size && /*#__PURE__*/_jsx(MainSection, {
          id: "client-showcase",
          color: "light",
          responsive: true,
          children: /*#__PURE__*/_jsxs(MainSectionInner, {
            children: [/*#__PURE__*/_jsx(H3, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-assets-ui.themes.details.clientExamples.title"
              })
            }), /*#__PURE__*/_jsx("p", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "marketplace-assets-ui.themes.details.clientExamples.subtitle"
              })
            }), /*#__PURE__*/_jsx(ThemeClientExamples, {
              clientExamples: clientExamples.toArray(),
              onTrackInteraction: trackInteraction
            })]
          })
        }), /*#__PURE__*/_jsx(MainSection, {
          id: "support",
          responsive: true,
          children: /*#__PURE__*/_jsx(MainSectionInner, {
            children: /*#__PURE__*/_jsxs(UIGrid, {
              children: [/*#__PURE__*/_jsxs(UIGridItem, {
                size: {
                  xs: 12,
                  xl: 8
                },
                children: [/*#__PURE__*/_jsx(H2, {
                  className: "is--heading-3",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "marketplace-assets-ui.themes.details.support.title"
                  })
                }), /*#__PURE__*/_jsx(ThemeSupport, {
                  chatUrl: chatUrl,
                  facebookUrl: facebookUrl,
                  getThemeEventSelectorId: getThemeEventSelectorId,
                  languageTagRenderer: renderSupportLanguageTag,
                  onTrackInteraction: trackInteraction,
                  phone: phone,
                  provider: provider,
                  responsive: true,
                  support: support,
                  supportEmail: supportEmail,
                  supportLanguages: supportLanguages.toArray(),
                  supportUrl: supportUrl
                })]
              }), /*#__PURE__*/_jsx(UIGridItem, {
                size: {
                  xs: 12,
                  xl: 4
                }
              })]
            })
          })
        }), !isAdminPreview && /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(MainSection, {
            id: "reviews",
            responsive: true,
            children: /*#__PURE__*/_jsx(MainSectionInner, {
              children: /*#__PURE__*/_jsx(Reviews, {
                name: name,
                exampleUrl: exampleUrl,
                isPreview: isPreview,
                offeringId: offeringId,
                onTrackInteraction: trackInteraction,
                overallRating: reviewSummary.overallRating,
                providerName: providerName,
                onReviewsFulfilled: setReviewsFulfilled,
                supportUrl: supportUrl,
                thumbnailUrl: thumbnailUrl,
                versionNumber: versionNumber
              })
            })
          }), /*#__PURE__*/_jsx(UIIntersectionObserver, {
            onIntersectionChange: ({
              entries,
              observer
            }) => {
              if (entries[0].isIntersecting) {
                observer.disconnect();
              }
            },
            renderContent: isIntersecting => {
              if (isIntersecting && !isEmbedded) {
                return /*#__PURE__*/_jsx(ThemeRecommendationsSection, {
                  businessTypes: businessTypes.toArray(),
                  isPreview: isPreview,
                  offeringId: offeringId,
                  onTrackInteraction: trackInteraction,
                  onTrackPageview: trackPageview,
                  providerName: providerName
                });
              }

              return /*#__PURE__*/_jsx("div", {});
            }
          })]
        })]
      }), featureCompatibilityModalOpen && /*#__PURE__*/_jsx(FeatureCompatibilityModal, {
        onClose: () => setFeatureCompatibilityModalOpen(false),
        themeScopes: themeScopes,
        trackInteraction: trackInteraction
      }), versionHistoryOpen && /*#__PURE__*/_jsx(ThemeVersionHistoryModalPanel, {
        offeringId: offeringId,
        onClose: () => setVersionHistoryOpen(false),
        onTrackInteraction: trackInteraction,
        onTrackPageview: trackPageview
      }), /*#__PURE__*/_jsx(HubsParamsTracker, {}, `theme-details-${slug}`)]
    })
  });
};

ThemeDetailsPage.propTypes = {
  themeDetails: PropTypes.instanceOf(ThemeDetailsResponse).isRequired,
  use: PropTypes.oneOf(['default', 'preview', 'admin-preview'])
};
export default ThemeDetailsPage;